import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { createMemo, For, Show } from "solid-js";
import { Calculator } from "@fleet/icons";
import {
  computeInventoryValue,
  fromMinorQty,
  getProductsQuery,
  getValuationLayersQuery,
  queryKeys,
} from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { appRoute } from "./route";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgId } from "@/features/inventory/use-org-id";
import { PageContainer, PageHeader } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { BarChart, type ChartDatum } from "@/components/ui/charts";
import type { ProductRecord, ValuationLayerRecord } from "@stockpilot/powersync";

function ValuationPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const orgId = useOrgId();

  const products = createPowerSyncQuery<ProductRecord>({
    queryKey: () => queryKeys.inventory.products(orgId()),
    queryFn: () => getProductsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const layers = createPowerSyncQuery<ValuationLayerRecord>({
    queryKey: () => queryKeys.inventory.valuationLayers(orgId()),
    queryFn: () => getValuationLayersQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const productName = createMemo(() => {
    const map = new Map((products.data ?? []).map((p) => [p.id, p.name]));
    return (id: string) => map.get(id) ?? id;
  });

  // On-hand inventory value per product, derived from layers (AVCO).
  // CC-6: pure calculation (computeInventoryValue), no lingui macros.
  const rows = createMemo(() =>
    computeInventoryValue(layers.data ?? []).filter((r) => r.onHand !== 0),
  );

  const totalValue = createMemo(() => rows().reduce((acc, r) => acc + r.value, 0));

  // Top products by inventory value — bar chart (theme-aware, inline).
  const valueChartData = createMemo<ChartDatum[]>(() =>
    [...rows()]
      .toSorted((a, b) => b.value - a.value)
      .slice(0, 8)
      .map((r) => ({
        label: productName()(r.productId),
        value: r.value,
        display: String(fromMinorQty(r.value)),
        colorClass: "text-primary",
      })),
  );

  return (
    <PageContainer>
      <PageHeader
        title={t(msg`Valuation`)}
        description={t(msg`On-hand inventory value by product (moving average cost).`)}
        icon={Calculator}
      />

      <div class="mt-6 rounded-md border border-border p-4" data-testid="valuation-summary">
        <p class="text-xs text-muted-foreground">{t(msg`Total inventory value`)}</p>
        <p class="text-2xl font-semibold tabular-nums" data-testid="valuation-total">
          {fromMinorQty(totalValue())}
        </p>
      </div>

      <Show when={valueChartData().length > 0}>
        <div
          class="mt-6 rounded-xl border border-border bg-card/60 p-5"
          data-testid="valuation-chart"
        >
          <h2 class="mb-4 text-sm font-semibold tracking-tight text-foreground/90">
            {t(msg`Top products by value`)}
          </h2>
          <BarChart ariaLabel={t(msg`Inventory value by product`)} data={valueChartData()} />
        </div>
      </Show>

      <div class="mt-6" data-testid="valuation-list">
        <Show
          when={rows().length > 0}
          fallback={
            <EmptyState
              icon={Calculator}
              title={t(msg`Nothing to value`)}
              description={t(msg`Receive a purchase order to build inventory value.`)}
            />
          }
        >
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border text-left text-muted-foreground">
                <th class="py-2 font-medium">{t(msg`Product`)}</th>
                <th class="py-2 font-medium text-right">{t(msg`On-hand`)}</th>
                <th class="py-2 font-medium text-right">{t(msg`Avg unit cost`)}</th>
                <th class="py-2 font-medium text-right">{t(msg`Value`)}</th>
              </tr>
            </thead>
            <tbody>
              <For each={rows()}>
                {(r) => (
                  <tr
                    class="border-b border-border/50"
                    data-testid="valuation-row"
                    data-product-id={r.productId}
                  >
                    <td class="py-2">{productName()(r.productId)}</td>
                    <td class="py-2 text-right tabular-nums" data-testid="valuation-onhand">
                      {fromMinorQty(r.onHand)}
                    </td>
                    <td class="py-2 text-right tabular-nums" data-testid="valuation-avgcost">
                      {fromMinorQty(r.avgUnitCost)}
                    </td>
                    <td
                      class="py-2 text-right tabular-nums font-medium"
                      data-testid="valuation-value"
                    >
                      {fromMinorQty(r.value)}
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </div>
    </PageContainer>
  );
}

export const valuationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/valuation",
  component: ValuationPage,
});
